import { ReadonlyVec2 } from 'gl-matrix';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import { Environment } from '~/engine/data/Environment';
import { ROTATION_CURSOR } from '~/engine/data/common/customs/cursors/rotationCursor';
import {
    FILLED_ARROWS_CURSOR,
    OUTLINED_ARROWS_CURSOR,
} from '~/engine/data/common/customs/cursors/translationCursor';
import { FusedImageDisplay } from '~/engine/view/layers/fusedImageDisplay';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { MOUSE_BUTTON_LEFT } from '../MouseButton';
import { CrosshairInteractionMode, Tool } from '../Tool';
import { RotationAction } from '../actions/registration-actions/RotationAction';
import { TranslationAction } from '../actions/registration-actions/TranslationAction';
import { isViewOfEnvironment } from './contouring-tools/editableView';

const NUDGE_KEYS: ReadonlySet<string> = new Set([
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
]);

/** Whether `key` with `modifierKeys` is the Ctrl/Cmd+Arrow nudge chord. */
function isNudgeChord(key: string, modifierKeys: ModifierKeys): boolean {
    return modifierKeys.hasCtrl && NUDGE_KEYS.has(key);
}

export enum RegistrationMode {
    TRANSLATION = 'translation',
    ROTATION = 'rotation',
    VOI = 'voi',
}
export class RegistrationTool extends Tool {
    public readonly toolType = 'registration';

    private readonly _translationAction: TranslationAction;
    private readonly _rotationAction: RotationAction;
    protected readonly _environment: Environment | undefined;
    private readonly _mode: RegistrationMode | null;
    private readonly _undoService: ReturnType<
        typeof getRegistrationUndoService
    >;

    public constructor(
        mode: RegistrationMode | null,
        environment: Environment | undefined
    ) {
        super();
        this._mode = mode;
        this._undoService = getRegistrationUndoService();
        this._translationAction = new TranslationAction(
            MOUSE_BUTTON_LEFT,
            this._undoService
        );
        this._rotationAction = new RotationAction(
            MOUSE_BUTTON_LEFT,
            this._undoService
        );
        this._environment = environment;
    }

    /**
     * The rotation arrow in rotation mode; in translation mode the
     * four-way arrows, filled while a translation is in progress; none
     * in the other modes.
     */
    public override get cursor(): string | undefined {
        if (this._mode === RegistrationMode.ROTATION) {
            return ROTATION_CURSOR;
        }
        if (this._mode === RegistrationMode.TRANSLATION) {
            return this._translationAction.isActive
                ? FILLED_ARROWS_CURSOR
                : OUTLINED_ARROWS_CURSOR;
        }
        return undefined;
    }

    public get currentMode(): RegistrationMode | null {
        return this._mode;
    }

    /**
     * All registration modes show roots-only crosshairs between drags and
     * silently disable during a drag.  VOI mode never has a stroke in
     * progress (the tool returns EVENT_NOT_HANDLED for mouse events), so
     * it always returns RootsOnly via the same path as translation /
     * rotation between drags.
     */
    public override getCrosshairInteractionMode(
        _isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.gizmoCrosshairMode(this.isStrokeInProgress());
    }

    public override isActionInProgress(): boolean {
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.isActive;
        }
        return this._translationAction.isActive;
    }

    public onActivate(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * In translation and rotation mode, the reason a view of another
     * Environment than the armed one is refused, or any view when none is
     * armed (fail closed). A view of the armed Environment that draws its
     * primary alone is refused too: the actions move the fused image, and
     * the move must show under the pointer. Any other view of the armed
     * Environment is accepted, turned or not: the actions move the image
     * in the view's own plane. VOI mode and no mode act on no press, so
     * they refuse no view.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        if (
            this._mode !== RegistrationMode.TRANSLATION &&
            this._mode !== RegistrationMode.ROTATION
        ) {
            return undefined;
        }
        if (!isViewOfEnvironment(view, this._environment)) {
            return localization.t('registration.otherImageTile');
        }
        return view.fusedImageDisplay === FusedImageDisplay.PrimaryOnly
            ? localization.t('registration.fusedImageNotShown')
            : undefined;
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        // The actions write the armed environment's registration matrix
        // using the pressed view's coordinate frame, so the press must
        // come from a view of that environment (fail closed if unarmed).
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const refusal =
            button === MOUSE_BUTTON_LEFT ? this.refusalFor(view) : undefined;
        if (refusal !== undefined) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (view.environment !== this._environment) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onMousePress(
                button,
                modifierKeys,
                coordinates,
                view,
                this._environment
            );
        }
        return this._translationAction.onMousePress(
            button,
            modifierKeys,
            coordinates,
            view,
            this._environment
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onMouseMove(
                coordinates,
                buttons,
                view,
                this._environment
            );
        }
        return this._translationAction.onMouseMove(
            coordinates,
            buttons,
            view,
            this._environment
        );
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2
    ): EventHandlerResult {
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        return this._mode === RegistrationMode.ROTATION
            ? this._rotationAction.onMouseRelease(
                  button,
                  coordinates,
                  undefined,
                  this._environment
              )
            : this._translationAction.onMouseRelease(
                  button,
                  coordinates,
                  undefined,
                  this._environment
              );
    }

    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: TextureView
    ): EventHandlerResult {
        if (key === 'Escape' && this.isActionInProgress()) {
            if (this._mode === RegistrationMode.ROTATION) {
                this._rotationAction.cancelAction(this._environment);
            } else if (this._mode === RegistrationMode.TRANSLATION) {
                this._translationAction.cancelAction(this._environment);
            } else {
                throw new Error('Unknown registration mode');
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        // A key nudge writes the armed environment's registration matrix in
        // the focused view's frame, so it is gated like a press. Escape above
        // is exempt: it cancels the armed action wherever focus is. A refused
        // nudge chord is consumed, or it would scroll the view instead.
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const keyRefusal = this.refusalFor(view);
        if (
            view.environment !== this._environment ||
            keyRefusal !== undefined
        ) {
            const refusal = isNudgeChord(key, modifierKeys)
                ? keyRefusal
                : undefined;
            if (refusal !== undefined) {
                enqueueSnackbar(refusal, { variant: 'warning' });
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED
                );
            }
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.VOI || this._mode === null) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this._mode === RegistrationMode.ROTATION) {
            return this._rotationAction.onKeyPress(
                key,
                modifierKeys,
                view,
                this._environment
            );
        }
        return this._translationAction.onKeyPress(
            key,
            modifierKeys,
            view,
            this._environment
        );
    }

    public onDeactivate(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
